// ===========================================
// Product API
// ===========================================
// API client for product catalog management (Phase 2 - Product Sales)
//
// Products can be sold individually or added to family invoices:
// - Music resources (method books, worksheets)
// - Music scores (sheet music, exam pieces)
// - Exam fees (AMEB, Trinity, etc.)
// - Merchandise (t-shirts, bags)
// - Equipment (metronomes, tuners)
// ===========================================

import { apiClient } from '../services/api';

// ===========================================
// TYPES
// ===========================================

export type ProductCategory =
  | 'MUSIC_RESOURCE'
  | 'MUSIC_SCORE'
  | 'EXAM_FEE'
  | 'MERCHANDISE'
  | 'EQUIPMENT'
  | 'OTHER';

export interface ProductImageThumbnails {
  sm: string;  // 100x100
  md: string;  // 300x300
  lg: string;  // 600x600
}

export interface ProductCategoryItem {
  id: string;
  schoolId: string;
  name: string;
  slug: string;
  description: string | null;
  color: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  _count?: { Product: number };
}

export interface CreateProductCategoryData {
  name: string;
  slug?: string;
  description?: string | null;
  color?: string | null;
  sortOrder?: number;
}

export interface UpdateProductCategoryData {
  name?: string;
  slug?: string;
  description?: string | null;
  color?: string | null;
  sortOrder?: number;
}

export interface Product {
  id: string;
  schoolId: string;
  name: string;
  description: string | null;
  sku: string | null;
  price: string; // Decimal comes as string from API
  category: ProductCategory;
  categoryId: string | null;
  ProductCategory?: ProductCategoryItem | null;
  imageUrl: string | null;
  imageThumbnails: ProductImageThumbnails | null;
  trackInventory: boolean;
  quantity: number | null;
  lowStockAlert: number | null;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  _count?: {
    InvoiceItem: number;
  };
}

export interface CreateProductInput {
  name: string;
  description?: string;
  sku?: string;
  price: number;
  category?: ProductCategory;
  categoryId?: string | null;
  imageUrl?: string;
  trackInventory?: boolean;
  quantity?: number;
  lowStockAlert?: number;
  sortOrder?: number;
}

export interface UpdateProductInput {
  name?: string;
  description?: string | null;
  sku?: string | null;
  price?: number;
  category?: ProductCategory;
  categoryId?: string | null;
  imageUrl?: string | null;
  trackInventory?: boolean;
  quantity?: number;
  lowStockAlert?: number | null;
  isActive?: boolean;
  sortOrder?: number;
}

export interface ProductFilters {
  category?: ProductCategory;
  categoryId?: string;
  isActive?: boolean;
  search?: string;
  trackInventory?: boolean;
}

export interface InventoryAdjustmentInput {
  adjustment: number;
  reason?: string;
}

export interface ProductAvailability {
  available: boolean;
  currentStock: number | null;
  message?: string;
}

// API Response types
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

const BASE_URL = '/products/admin/products';
const CATEGORY_BASE_URL = '/products/admin/categories';

// ===========================================
// API FUNCTIONS
// ===========================================

/**
 * Get all products with optional filters
 * Access: Admin only
 */
export async function getProducts(filters?: ProductFilters): Promise<Product[]> {
  const response = await apiClient.get<ApiResponse<Product[]>>(BASE_URL, {
    params: filters,
  });
  return response.data;
}

/**
 * Get a single product by ID
 * Access: Admin only
 */
export async function getProduct(id: string): Promise<Product> {
  const response = await apiClient.get<ApiResponse<Product>>(`${BASE_URL}/${id}`);
  return response.data;
}

/**
 * Get products with low stock alerts
 * Access: Admin only
 */
export async function getLowStockProducts(): Promise<Product[]> {
  const response = await apiClient.get<ApiResponse<Product[]>>(`${BASE_URL}/low-stock`);
  return response.data;
}

export interface ProductsByCategory {
  categoryId: string | null;
  categoryName: string;
  products: Product[];
}

/**
 * Get products grouped by category
 * Access: Admin only
 */
export async function getProductsByCategory(): Promise<ProductsByCategory[]> {
  const response = await apiClient.get<ApiResponse<ProductsByCategory[]>>(
    `${BASE_URL}/by-category`
  );
  return response.data;
}

/**
 * Create a new product
 * Access: Admin only
 */
export async function createProduct(data: CreateProductInput): Promise<Product> {
  const response = await apiClient.post<ApiResponse<Product>>(BASE_URL, data);
  return response.data;
}

/**
 * Update a product
 * Access: Admin only
 */
export async function updateProduct(id: string, data: UpdateProductInput): Promise<Product> {
  const response = await apiClient.patch<ApiResponse<Product>>(`${BASE_URL}/${id}`, data);
  return response.data;
}

/**
 * Delete a product (soft delete if used in invoices)
 * Access: Admin only
 */
export async function deleteProduct(id: string): Promise<void> {
  await apiClient.delete(`${BASE_URL}/${id}`);
}

/**
 * Adjust product inventory
 * Access: Admin only
 */
export async function adjustInventory(
  id: string,
  data: InventoryAdjustmentInput
): Promise<Product> {
  const response = await apiClient.post<ApiResponse<Product>>(`${BASE_URL}/${id}/inventory`, data);
  return response.data;
}

/**
 * Check product availability for a given quantity
 * Access: Admin only
 */
export async function checkAvailability(
  id: string,
  quantity: number
): Promise<ProductAvailability> {
  const response = await apiClient.post<ApiResponse<ProductAvailability>>(
    `${BASE_URL}/${id}/check-availability`,
    { quantity }
  );
  return response.data;
}

// ===========================================
// HELPER FUNCTIONS
// ===========================================

/**
 * Format product price for display
 */
export function formatProductPrice(price: string | number): string {
  const numPrice = typeof price === 'string' ? parseFloat(price) : price;
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
  }).format(numPrice);
}

/**
 * Get stock status for a product
 */
export function getStockStatus(
  product: Product
): 'in_stock' | 'low_stock' | 'out_of_stock' | 'not_tracked' {
  if (!product.trackInventory) {
    return 'not_tracked';
  }

  const quantity = product.quantity ?? 0;
  const threshold = product.lowStockAlert ?? 0;

  if (quantity === 0) {
    return 'out_of_stock';
  }

  if (quantity <= threshold) {
    return 'low_stock';
  }

  return 'in_stock';
}

/**
 * Get stock status label
 */
export function getStockStatusLabel(status: ReturnType<typeof getStockStatus>): string {
  switch (status) {
    case 'in_stock':
      return 'In Stock';
    case 'low_stock':
      return 'Low Stock';
    case 'out_of_stock':
      return 'Out of Stock';
    case 'not_tracked':
      return 'Not Tracked';
  }
}

/**
 * Get stock status color for MUI components
 */
export function getStockStatusColor(
  status: ReturnType<typeof getStockStatus>
): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'in_stock':
      return 'success';
    case 'low_stock':
      return 'warning';
    case 'out_of_stock':
      return 'error';
    case 'not_tracked':
      return 'default';
  }
}

// ===========================================
// PRODUCT CATEGORY API FUNCTIONS
// ===========================================

export async function getProductCategories(): Promise<ProductCategoryItem[]> {
  const response = await apiClient.get<ApiResponse<ProductCategoryItem[]>>(CATEGORY_BASE_URL);
  return response.data;
}

export async function createProductCategory(
  data: CreateProductCategoryData
): Promise<ProductCategoryItem> {
  const response = await apiClient.post<ApiResponse<ProductCategoryItem>>(CATEGORY_BASE_URL, data);
  return response.data;
}

export async function updateProductCategory(
  categoryId: string,
  data: UpdateProductCategoryData
): Promise<ProductCategoryItem> {
  const response = await apiClient.patch<ApiResponse<ProductCategoryItem>>(
    `${CATEGORY_BASE_URL}/${categoryId}`,
    data
  );
  return response.data;
}

export async function deleteProductCategory(categoryId: string): Promise<void> {
  await apiClient.delete(`${CATEGORY_BASE_URL}/${categoryId}`);
}

// ===========================================
// ADD PRODUCT TO INVOICES TYPES
// ===========================================

export type ProductInvoiceTargetType = 'SCHOOL' | 'AGE_GROUP' | 'PROGRAM_LEVEL' | 'CLASS' | 'FAMILY';

export type ProductQuantityMode = 'PER_FAMILY' | 'PER_STUDENT';

export interface AddProductToInvoicesInput {
  targetType: ProductInvoiceTargetType;
  targetId?: string;
  /**
   * FAMILY only: several families in one operation. Takes precedence over
   * `targetId` server-side. The other four target types are inherently single and
   * the server rejects `targetIds` for them.
   */
  targetIds?: string[];
  quantity: number;
  quantityMode?: ProductQuantityMode;
  unitPrice?: number;
  termId?: string;
  dueDate?: string;
}

export interface AddProductToInvoicesPreview {
  product: {
    id: string;
    name: string;
    price: number;
    category: string;
    trackInventory: boolean;
    currentStock: number | null;
  };
  targetDescription: string;
  families: Array<{
    familyId: string;
    familyName: string;
    action: 'UPDATE' | 'CREATE';
    existingInvoiceNumber?: string;
    studentCount: number;
    effectiveQuantity: number;
  }>;
  summary: {
    totalFamilies: number;
    totalStudents: number;
    existingDraftInvoices: number;
    newInvoicesToCreate: number;
    totalQuantityNeeded: number;
    inventorySufficient: boolean;
    totalValueToAdd: number;
  };
}

export interface AddProductToInvoicesResult {
  summary: {
    updated: number;
    created: number;
    skipped: number;
    totalFamilies: number;
    totalValueAdded: number;
    inventoryDeducted: number;
  };
  families: Array<{
    familyId: string;
    familyName: string;
    action: 'UPDATED' | 'CREATED' | 'SKIPPED';
    invoiceId?: string;
    invoiceNumber?: string;
    reason?: string;
  }>;
  errors: Array<{ familyId: string; familyName: string; error: string }>;
}

// ===========================================
// ADD PRODUCT TO INVOICES API
// ===========================================

/**
 * Preview adding a product to invoices for target audience
 * Access: Admin only
 */
export async function previewAddProductToInvoices(
  productId: string,
  data: AddProductToInvoicesInput
): Promise<AddProductToInvoicesPreview> {
  const response = await apiClient.post<ApiResponse<AddProductToInvoicesPreview>>(
    `${BASE_URL}/${productId}/add-to-invoices/preview`,
    data
  );
  return response.data;
}

/**
 * Add product to invoices for target audience
 * Access: Admin only
 */
export async function addProductToInvoices(
  productId: string,
  data: AddProductToInvoicesInput
): Promise<AddProductToInvoicesResult> {
  const response = await apiClient.post<ApiResponse<AddProductToInvoicesResult>>(
    `${BASE_URL}/${productId}/add-to-invoices`,
    data
  );
  return response.data;
}

// ===========================================
// PRODUCT IMAGE API FUNCTIONS
// ===========================================

export interface ProductImageUploadResult {
  imageUrl: string;
  imageThumbnails: ProductImageThumbnails;
}

/**
 * Upload image for a product
 * Access: Admin only
 */
export async function uploadProductImage(
  productId: string,
  file: File
): Promise<ProductImageUploadResult> {
  const formData = new FormData();
  formData.append('image', file);

  const response = await apiClient.post<ApiResponse<ProductImageUploadResult>>(
    `${BASE_URL}/${productId}/image`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
}

/**
 * Delete image from a product
 * Access: Admin only
 */
export async function deleteProductImage(productId: string): Promise<void> {
  await apiClient.delete(`${BASE_URL}/${productId}/image`);
}

/**
 * Get product image info
 * Access: Admin only
 */
export async function getProductImageInfo(
  productId: string
): Promise<{ imageUrl: string | null; imageThumbnails: ProductImageThumbnails | null }> {
  const response = await apiClient.get<
    ApiResponse<{ imageUrl: string | null; imageThumbnails: ProductImageThumbnails | null }>
  >(`${BASE_URL}/${productId}/image`);
  return response.data;
}
